/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Divider, RequiredValidation } from 'sarsaparilla';
import get from 'lodash/get';
import { getClassName } from '../../utils/validationUtils';

const propTypes = {
    issuance: PropTypes.shape({
        id: PropTypes.string,
        is_nonprofit_org: PropTypes.bool,
        org_name: PropTypes.string,
        org_type: PropTypes.string,
    }),
    handleIsNonProfitOrgChange: PropTypes.func,
    handleOrgTypeChange: PropTypes.func,
    handleOrgNameChange: PropTypes.func,
    formValidationChange: PropTypes.func,
    validation: PropTypes.shape({}),
    displayValidationError: PropTypes.bool,
    isDisabled: PropTypes.bool,
};

const defaultProps = {
    isDisabled: false,
};

export default class NonProfitOrg extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isNonProfitOrg: props.issuance.is_nonprofit_org,
            orgType: props.issuance.org_type,
            orgName: props.issuance.org_name,
        };
        this.handleIsNonProfitOrgChange = this.handleIsNonProfitOrgChange.bind(this);
        this.handleOrgTypeChange = this.handleOrgTypeChange.bind(this);
        this.handleOrgNameChange = this.handleOrgNameChange.bind(this);
    }

    onFormValidate(isInValid, error, path) {
        if (isInValid) {
            this.props.formValidationChange(`${this.props.issuance.id}_${path}`, error);
        } else {
            this.props.formValidationChange(`${this.props.issuance.id}_${path}`, null);
        }
    }

    handleIsNonProfitOrgChange(event) {
        const value = event.target.checked;
        this.setState({
            isNonProfitOrg: value,
        });
        this.props.handleIsNonProfitOrgChange(value);
    }

    handleOrgTypeChange(event) {
        const value = event.target.value;
        this.setState({
            orgType: value,
        });
        this.props.handleOrgTypeChange(value);
    }

    handleOrgNameChange(event) {
        const value = event.target.value;
        this.setState({
            orgName: value,
        });
        this.props.handleOrgNameChange(value);
    }

    render() {
        const { issuance, validation, isDisabled, displayValidationError } = this.props;
        if (!issuance) {
            return null;
        }
        const orgTypeFailValidation = getClassName(
            get(validation, `${issuance.id}_org_type`),
            displayValidationError
        );
        const orgNameFailValidation = getClassName(
            get(validation, `${issuance.id}_org_name`),
            displayValidationError
        );

        return (
            <div>
                <h2 className={'h5'}>Non-Profit Organization</h2>
                <p>
                    A non-profit organization (NPO) may reserve a limited number of
                    permits under the organization’s name, allowing any member of the
                    organization to become the Group Leader at the time of permit pickup.
                    A maximum of three reservations are allowed under the organization’s
                    name per season. An NPO using this option must be prepared to show
                    evidence of legal non-profit status when picking up the permit.
                </p>
                <div className="rec-form-check-wrap">
                    <label
                        htmlFor="is-nonprofit"
                        className={`rec-label-checkbox ${isDisabled ? 'disabled' : ''}`}
                    >
                        <input
                            className={`rec-input-hide ${isDisabled ? 'disabled' : ''}`}
                            type="checkbox"
                            id="is-nonprofit"
                            checked={this.state.isNonProfitOrg}
                            disabled={isDisabled}
                            name="checkbox"
                            onChange={this.handleIsNonProfitOrgChange}
                        />
                        <span
                            className={`rec-input-checkbox ${isDisabled ? 'disabled' : ''}`}
                        />
                        Make the reservation under the non-profit organization.
                    </label>
                </div>
                {this.state.isNonProfitOrg && (
                    <div className="rec-section-outer-wrap">
                        <div className="rec-nested-wrap">
                            <div className="rec-grid-4-12">
                                <div className="rec-form-item-wrap">
                                    <label htmlFor="org-type">
                                        Type of Organization
                                        <mark title="required">*</mark>
                                    </label>
                                    <select
                                        id="org-type"
                                        className={orgTypeFailValidation}
                                        value={this.state.orgType}
                                        disabled={isDisabled}
                                        onChange={this.handleOrgTypeChange}
                                    >
                                        <option value="">----select----</option>
                                        <option value="Association">Association</option>
                                        <option value="Camp">Camp</option>
                                        <option value="Church">Church</option>
                                        <option value="School">School</option>
                                        <option value="Scouts">Scouts</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    <RequiredValidation
                                        value={get(this.state, 'orgType', '')}
                                        displayInSummary
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `org_type`
                                            )
                                        }
                                        controlLabel="Non-Profit Organization Type"
                                    />
                                </div>
                            </div>
                            <div className="rec-grid-4-12">
                                <div className="rec-form-item-wrap">
                                    {this.state.orgType === 'Scouts' && (
                                        <label htmlFor="guide-last-name">
                                            Organization Name/Troop #
                                            <mark title="required">*</mark>
                                        </label>
                                    )}
                                    {this.state.orgType !== 'Scouts' && (
                                        <label htmlFor="guide-last-name">
                                            Organization Name
                                            <mark title="required">*</mark>
                                        </label>
                                    )}
                                    <input
                                        type="text"
                                        id="org-name"
                                        className={orgNameFailValidation}
                                        disabled={isDisabled}
                                        value={this.state.orgName}
                                        onChange={this.handleOrgNameChange}
                                    />
                                    <RequiredValidation
                                        value={get(this.state, 'orgName', '')}
                                        displayInSummary
                                        onValidated={(isInValid, error) =>
                                            this.onFormValidate(
                                                isInValid,
                                                error,
                                                `org_name`
                                            )
                                        }
                                        controlLabel="Non-Profit Organization Name"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <Divider />
            </div>
        );
    }
}

NonProfitOrg.propTypes = propTypes;
NonProfitOrg.defaultProps = defaultProps;
